import React, { useRef, useContext } from 'react';
import classnames from 'classnames';
import numeral from 'numeral';
import { useTranslation } from 'react-i18next';
import { Url, URLS } from '@wix/da-url';
import { SessionContext } from '@wix/da-react-context/pkg/SessionContext';
import { ThemeContext } from '@wix/da-react-context/pkg/ThemeContext';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiData } from '@wix/da-bi/pkg/events';
import shortenNumber from '@wix/da-ds/pkg/utils/shortenNumber';
import { useCurrentPromo } from '../../../../utils/hooks/useCurrentPromo';
import UserLink from '../../../../User/Link';
import MenuList from '@wix/da-ds/pkg/Dropdown/MenuList';
import SaleBanner from '../../../../banners/SaleBanner';
import LogoutItem from '../../../_partials/LogoutItem';
import CoreMenuItem from '../../../_partials/CoreMenuItem';
import VerificationReminder from '../../../_partials/VerificationReminder';
import AISuppressionSettingItem from '../../../_partials/AISuppressionSettingItem';
import UserMenuAvatar from './UserMenuAvatar';
import { getContentFilterItem, getThemeToggle } from './menuItemHelpers';
import userMenuStyles from './UserMenu.scss';
function formatNumber(number) {
    return number > 999 ? shortenNumber(number, 0, Math.floor) : number;
}
const LoggedInUserMenu = ({ currentUser, isUserMenuOpened, hasSubscriptions, disableThemeSwitcher, contentFilter, toggleTheme, onToggleUserMenu, onUserLogoutButtonClicked, pushUserSettingsModal })=>{
    const theme = useContext(ThemeContext);
    const { csrfToken, verificationEmail, hasCoreMembership, coreLevel, counts } = useContext(SessionContext);
    const currentPromo = useCurrentPromo('self');
    const { t } = useTranslation();
    const logoutItemRef = useRef(null);
    const handleLogout = ()=>{
        onUserLogoutButtonClicked();
        // Avoid a race condition and let sagas do their thing before submitting the form
        setTimeout(()=>{
            logoutItemRef.current && logoutItemRef.current.submit();
        }, 100);
    };
    const tabIndex = isUserMenuOpened ? 0 : -1;
    const subscriptionsUrl = Url.userSubscriptionsLink({
        username: currentUser.username
    });
    const personalGroupItems = [
        {
            label: /*#__PURE__*/ React.createElement("div", {
                className: userMenuStyles['two-side-label']
            }, /*#__PURE__*/ React.createElement("span", null, t('siteHeaderMobile.userMenu.notes')), !!(counts === null || counts === void 0 ? void 0 : counts.notes) && /*#__PURE__*/ React.createElement("span", {
                className: userMenuStyles['menu-item-notification']
            }, formatNumber(counts === null || counts === void 0 ? void 0 : counts.notes))),
            link: Url.buildFromPath(URLS.NOTES),
            biData: BiData({
                evid: 702,
                link_url: Url.buildFromPath(URLS.NOTES),
                menu_item: 'notes',
                menu_name: 'profile'
            }),
            tabIndex
        },
        hasSubscriptions && {
            label: t('siteHeader.userMenu.mySubscriptions'),
            link: subscriptionsUrl,
            biData: BiData({
                evid: 702,
                link_url: subscriptionsUrl,
                menu_item: 'my_subscriptions',
                menu_name: 'profile'
            }),
            tabIndex
        }
    ];
    var _counts_points, _counts_fragments;
    const statsGroupItems = [
        {
            label: t('siteHeaderMobile.userMenu.monetizeMyArt'),
            newTag: true,
            link: `${URLS.DEVIANTART_SELL}?utm_source=DA&utm_medium=navbar&utm_campaign=DA_mobile_navbar&utm_id=DA_mobile_nav`,
            target: '_blank',
            biData: BiData({
                evid: 702,
                link_url: URLS.DEVIANTART_SELL,
                menu_item: 'monetizeMyArt',
                menu_name: 'profile'
            }),
            tabIndex
        },
        {
            label: /*#__PURE__*/ React.createElement("div", {
                className: userMenuStyles['two-side-label']
            }, /*#__PURE__*/ React.createElement("span", null, t('siteHeaderMobile.userMenu.pointsBalance')), /*#__PURE__*/ React.createElement("span", null, numeral((_counts_points = counts === null || counts === void 0 ? void 0 : counts.points) !== null && _counts_points !== void 0 ? _counts_points : 0).format('0,0'))),
            link: Url.buildFromPath(URLS.POINTS),
            biData: BiData({
                evid: 702,
                link_url: Url.buildFromPath(URLS.POINTS),
                menu_item: 'pointsBalance',
                menu_name: 'profile'
            }),
            tabIndex
        },
        {
            label: /*#__PURE__*/ React.createElement("div", {
                className: userMenuStyles['two-side-label']
            }, /*#__PURE__*/ React.createElement("span", null, t('siteHeader.userMenu.fragmentsBalance')), /*#__PURE__*/ React.createElement("span", null, numeral((_counts_fragments = counts === null || counts === void 0 ? void 0 : counts.fragments) !== null && _counts_fragments !== void 0 ? _counts_fragments : 0).format('0,0'))),
            link: 'https://www.deviantartsupport.com/en/article/what-are-badges-and-fragments',
            target: '_blank',
            biData: BiData({
                evid: 702,
                menu_item: 'fragments',
                menu_name: 'profile'
            }),
            tabIndex
        },
        {
            label: t('siteHeaderMobile.userMenu.myEarnings'),
            link: Url.buildFromPath(URLS.EARNINGS),
            biData: BiData({
                evid: 702,
                link_url: Url.buildFromPath(URLS.EARNINGS),
                menu_item: 'myEarnings',
                menu_name: 'profile'
            }),
            tabIndex
        },
        {
            label: t('siteHeaderMobile.userMenu.myOrderHistory'),
            link: Url.buildFromPath(URLS.ORDERS),
            biData: BiData({
                evid: 702,
                link_url: Url.buildFromPath(URLS.ORDERS),
                menu_item: 'order_history',
                menu_name: 'profile'
            }),
            tabIndex
        },
        {
            label: t('siteHeaderMobile.userMenu.customerDashboard'),
            link: Url.buildFromPath(URLS.CUSTOMER_DASHBOARD),
            biData: BiData({
                evid: 702,
                link_url: Url.buildFromPath(URLS.CUSTOMER_DASHBOARD),
                menu_item: 'customer_dashboard',
                menu_name: 'profile'
            }),
            tabIndex
        }
    ];
    const lastGroupItems = [
        !disableThemeSwitcher && getThemeToggle(theme, toggleTheme, t),
        getContentFilterItem(contentFilter, pushUserSettingsModal, t),
        {
            label: /*#__PURE__*/ React.createElement(AISuppressionSettingItem, {
                isLoggedIn: true
            })
        },
        {
            label: t('siteHeaderMobile.userMenu.settings'),
            link: Url.buildFromPath(URLS.SETTINGS),
            className: userMenuStyles['settings-link'],
            biData: BiData({
                evid: 702,
                link_url: Url.buildFromPath(URLS.SETTINGS),
                menu_item: 'settings',
                menu_name: 'profile'
            }),
            tabIndex
        },
        {
            label: t('siteHeader.userMenu.helpAndFAQ'),
            link: URLS.HELP,
            biData: BiData({
                evid: 702,
                link_url: URLS.HELP,
                menu_item: 'help',
                menu_name: 'profile'
            }),
            tabIndex
        },
        {
            label: /*#__PURE__*/ React.createElement(LogoutItem, {
                formRef: logoutItemRef,
                token: csrfToken,
                label: t('siteHeaderMobile.userMenu.logOut')
            }),
            onClick: handleLogout,
            biData: BiData({
                evid: 702,
                link_url: Url.buildFromPath(URLS.LOGOUT),
                menu_item: 'logout',
                menu_name: 'profile'
            }),
            tabIndex
        }
    ];
    const coreLinkUrl = !hasCoreMembership ? URLS.CORE_MEMBERSHIP : URLS.SETTINGS_BILLING;
    return /*#__PURE__*/ React.createElement(MenuList, {
        renderOnSSR: true,
        id: "site-header-user-menu",
        className: classnames(userMenuStyles['user-menu']),
        itemsFillContainerWidth: true,
        items: [
            {
                className: userMenuStyles['top-group'],
                items: [
                    {
                        label: /*#__PURE__*/ React.createElement("div", {
                            className: userMenuStyles['two-side-label']
                        }, /*#__PURE__*/ React.createElement(UserLink, {
                            user: currentUser,
                            className: userMenuStyles['user-link'],
                            showSymbol: false,
                            tabIndex: tabIndex
                        }), /*#__PURE__*/ React.createElement(UserMenuAvatar, {
                            currentUser: currentUser,
                            onToggleUserMenu: onToggleUserMenu,
                            isUserMenuOpened: isUserMenuOpened,
                            tabIndex: tabIndex
                        }))
                    },
                    {
                        label: /*#__PURE__*/ React.createElement(CoreMenuItem, {
                            className: userMenuStyles['two-side-label'],
                            hasCoreMembership: hasCoreMembership,
                            coreLevel: coreLevel
                        }),
                        link: coreLinkUrl,
                        biData: BiData({
                            evid: 702,
                            menu_item: hasCoreMembership ? 'my_core' : 'core_membership',
                            menu_name: 'profile',
                            link_url: coreLinkUrl
                        }),
                        tabIndex
                    }
                ]
            },
            currentPromo && {
                label: /*#__PURE__*/ React.createElement(SaleBanner, {
                    className: userMenuStyles['sale-banner'],
                    isNarrow: true,
                    endDate: currentPromo.endDate,
                    discountValuePercent: currentPromo.discountValuePercent,
                    referralType: ReferralType.PROMOTION_BANNER_USERMENU
                })
            },
            !verificationEmail && {
                className: userMenuStyles['group'],
                items: personalGroupItems
            },
            verificationEmail ? {
                label: /*#__PURE__*/ React.createElement(VerificationReminder, {
                    email: verificationEmail
                }),
                className: userMenuStyles['verification-group']
            } : {
                className: userMenuStyles['group'],
                items: statsGroupItems
            },
            {
                className: userMenuStyles['group'],
                items: lastGroupItems
            }
        ]
    });
};
LoggedInUserMenu.displayName = 'LoggedInUserMenu';
export default LoggedInUserMenu;
